import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Form, message, Spin, Tooltip } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  CustomButton,
  CustomModalWithoutButton,
  CustomShipmentText,
  ToggleSwitch,
} from '@/components';
import CustomCheckboxGroup from '@/components/CustomCheckboxGroup';
import type {
  GeoFenceRecipientTeamPayload,
  ShipmentGeoFence,
  LatLng,
  SaveGeoFencePayload,
} from '@/apis/types';
import AlertRecipientSelector from '../PerShipmentNotification/components/AlertRecipientSelector';
import PolygonFenceMap from '@/components/PolygonFenceMap';
import { ChevronDownIcon, UndoIcon } from '@/assets/icons/icons';
import usePolygonEditSession from '@/hooks/usePolygonEditSession';
import useFetchGeoFenceRecipients from '../hooks/useFetchGeoFenceRecipients';
import { useToken } from '@/common/helper';
import {
  DeleteShipmentGeoFence,
  GetShipmentGeoFence,
  SaveShipmentGeoFence,
} from '@/apis/shipment';

export interface GeoFenceLocationOption {
  id: string;
  stopKey?: string;
  menuLabel?: string;
  name: string;
  address: string;
  coordinates: LatLng;
  locationId?: string | null;
  executionLegId?: string | null;
  hasDefaultFence?: boolean;
  hasPerShipmentFence?: boolean;
}

interface GeoFenceLocationsModalProps {
  open: boolean;
  shipmentId: string;
  location: GeoFenceLocationOption | null;
  savedFence?: GeoFenceDraft;
  onClose: () => void;
  onApply: (locationId: string, draft: GeoFenceDraft) => void;
  // Called after the per-shipment override is removed.
  onRemoved?: (locationId: string) => void;
}

interface RecipientTeamSelection {
  id?: string;
  team_id?: string;
  select_all?: boolean;
  users?: Array<string | { id?: string | number }>;
}

export interface GeoFenceDraft {
  polygon: LatLng[];
  closed: boolean;
  entersFence: boolean;
  exitsFence: boolean;
  selectedRecipients: string[];
  recipientsValues: unknown[];
  additionalRecipients: string[];
  isMarkAsDelivered?: boolean;
}

type GeoFenceDetailPayload = Partial<ShipmentGeoFence> &
  Partial<SaveGeoFencePayload> & {
    closed?: boolean;
  };

type GeoFenceDetailCandidate = GeoFenceDetailPayload & {
  geo_fence?: GeoFenceDetailPayload;
  geofence?: GeoFenceDetailPayload;
  fence?: GeoFenceDetailPayload;
  data?: GeoFenceDetailCandidate;
};

const isLatLng = (point: unknown): point is LatLng => {
  if (!point || typeof point !== 'object') return false;
  const candidate = point as Partial<LatLng>;
  return (
    typeof candidate.lat === 'number' &&
    Number.isFinite(candidate.lat) &&
    typeof candidate.lng === 'number' &&
    Number.isFinite(candidate.lng)
  );
};

const unwrapGeoFenceDetail = (response: unknown): GeoFenceDetailPayload => {
  const payload = response as GeoFenceDetailCandidate & {
    data?: {
      data?: GeoFenceDetailCandidate | { data?: GeoFenceDetailCandidate };
    };
  };
  const responseData =
    payload?.data?.data &&
    typeof payload.data.data === 'object' &&
    'data' in payload.data.data
      ? payload.data.data.data
      : payload?.data?.data;

  const candidates = [
    responseData?.geo_fence,
    responseData?.geofence,
    responseData?.fence,
    responseData,
    payload?.geo_fence,
    payload?.geofence,
    payload?.fence,
    payload,
  ];

  return (candidates.find(
    (candidate) =>
      candidate &&
      typeof candidate === 'object' &&
      ('polygon' in candidate ||
        'notify_on_enter' in candidate ||
        'notify_on_exit' in candidate ||
        'recipients' in candidate)
  ) ?? {}) as GeoFenceDetailPayload;
};

const getSelectedRecipientKeys = (
  additional: string[],
  teams: GeoFenceRecipientTeamPayload[]
) => [
  ...additional,
  ...teams.flatMap((team) =>
    team.select_all
      ? [`${team.team_id}::ALL_MEMBERS`]
      : (team.user_ids ?? []).map((userId) => `${team.team_id}::${userId}`)
  ),
];

const mapGeoFenceDetailToDraft = (
  detail: GeoFenceDetailPayload
): GeoFenceDraft => {
  const polygon = Array.isArray(detail.polygon)
    ? detail.polygon.filter(isLatLng)
    : [];
  const teams = detail.recipients?.teams ?? [];
  const additional = detail.recipients?.additional ?? [];

  return {
    polygon,
    closed: detail.closed ?? polygon.length >= 3,
    entersFence: detail.notify_on_enter ?? true,
    exitsFence: detail.notify_on_exit ?? false,
    selectedRecipients: getSelectedRecipientKeys(additional, teams),
    recipientsValues: teams.map((team) => ({
      id: team.team_id,
      select_all: team.select_all,
      users: team.user_ids ?? [],
    })),
    additionalRecipients: additional,
    isMarkAsDelivered: detail.mark_as_delivered ?? false,
  };
};

const getUniqueValues = (values: string[]) => Array.from(new Set(values));

const ENTERS_FENCE = 'enters_geo_fence';
const EXITS_FENCE = 'exits_geo_fence';

const getRecipientTeamId = (team: RecipientTeamSelection) =>
  team.team_id ?? team.id;

const getRecipientUserIds = (users: RecipientTeamSelection['users'] = []) =>
  users
    .map((user) => {
      if (typeof user === 'string') return user;
      if (typeof user?.id === 'string' || typeof user?.id === 'number') {
        return String(user.id);
      }
      return null;
    })
    .filter((userId): userId is string => Boolean(userId));

const buildGeoFenceRecipientTeams = (
  selected: string[],
  values: unknown[]
): GeoFenceRecipientTeamPayload[] => {
  const teamsById = new Map<string, GeoFenceRecipientTeamPayload>();

  (values as RecipientTeamSelection[]).forEach((team) => {
    const teamId = getRecipientTeamId(team);
    if (!teamId) return;

    teamsById.set(String(teamId), {
      team_id: String(teamId),
      select_all: Boolean(team.select_all),
      user_ids: getRecipientUserIds(team.users),
    });
  });

  selected.forEach((recipientKey) => {
    if (!recipientKey.includes('::')) return;

    const [teamId, userId] = recipientKey.split('::');
    if (!teamId || !userId) return;

    const existing = teamsById.get(teamId) ?? {
      team_id: teamId,
      select_all: false,
      user_ids: [],
    };

    if (userId === 'ALL_MEMBERS') {
      existing.select_all = true;
      existing.user_ids = [];
    } else if (!existing.select_all) {
      existing.user_ids = getUniqueValues([
        ...(existing.user_ids ?? []),
        userId,
      ]);
    }

    teamsById.set(teamId, existing);
  });

  return Array.from(teamsById.values())
    .filter((team) => team.select_all || (team.user_ids?.length ?? 0) > 0)
    .map((team) => {
      if (team.select_all) {
        return {
          team_id: team.team_id,
          select_all: true,
        };
      }

      return {
        team_id: team.team_id,
        select_all: false,
        user_ids: team.user_ids ?? [],
      };
    });
};

const GeoFenceLocationsModal: React.FC<GeoFenceLocationsModalProps> = ({
  open,
  shipmentId,
  location,
  savedFence,
  onClose,
  onApply,
  onRemoved,
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { recipientData } = useFetchGeoFenceRecipients(shipmentId);
  const { checkTokenExpiry, decodedUserToken } = useToken();
  const checkTokenExpiryRef = useRef(checkTokenExpiry);
  const loggedInUserId = decodedUserToken()?.email;
  const [entersFence, setEntersFence] = useState(true);
  const [exitsFence, setExitsFence] = useState(false);
  const polygonSession = usePolygonEditSession();
  const { load: loadPolygonSession } = polygonSession;
  const [validationMessage, setValidationMessage] = useState('');
  const [isRecipientsDropdownOpen, setIsRecipientsDropdownOpen] =
    useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [recipientsValues, setRecipientsValues] = useState<unknown[]>([]);
  const [additionalRecipients, setAdditionalRecipients] = useState<string[]>(
    []
  );
  const [isMarkAsDelivered, setIsMarkAsDelivered] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingFence, setIsLoadingFence] = useState(false);
  const [isRemovingFence, setIsRemovingFence] = useState(false);

  useEffect(() => {
    form.setFieldValue('notification_method', 'EMAIL');
  }, [form]);

  useEffect(() => {
    checkTokenExpiryRef.current = checkTokenExpiry;
  }, [checkTokenExpiry]);

  useEffect(() => {
    if (!open) return;
    const initialDraft = savedFence;
    setEntersFence(initialDraft?.entersFence ?? true);
    setExitsFence(initialDraft?.exitsFence ?? false);
    loadPolygonSession(
      initialDraft?.polygon ?? [],
      initialDraft?.closed ?? false
    );
    setSelectedRecipients(initialDraft?.selectedRecipients ?? []);
    setRecipientsValues(initialDraft?.recipientsValues ?? []);
    setAdditionalRecipients(initialDraft?.additionalRecipients ?? []);
    setIsMarkAsDelivered(initialDraft?.isMarkAsDelivered ?? false);
    setValidationMessage('');
  }, [open, location?.id, savedFence, loadPolygonSession]);

  const canMarkAsDelivered = !polygonSession.isEmpty && entersFence;

  useEffect(() => {
    if (!canMarkAsDelivered) setIsMarkAsDelivered(false);
  }, [canMarkAsDelivered]);

  const activeLocation = location as GeoFenceLocationOption;

  useEffect(() => {
    if (!open || !location?.hasPerShipmentFence) return;

    const token = checkTokenExpiryRef.current();
    if (!token) return;

    let isActive = true;
    setIsLoadingFence(true);

    GetShipmentGeoFence(token, shipmentId, location.stopKey ?? location.id)
      .then((response) => {
        if (!isActive) return;
        const draft = mapGeoFenceDetailToDraft(unwrapGeoFenceDetail(response));
        setEntersFence(draft.entersFence);
        setExitsFence(draft.exitsFence);
        loadPolygonSession(draft.polygon, draft.closed);
        setSelectedRecipients(draft.selectedRecipients);
        setRecipientsValues(draft.recipientsValues);
        setAdditionalRecipients(draft.additionalRecipients);
        setValidationMessage('');
        setIsMarkAsDelivered(draft.isMarkAsDelivered);
      })
      .catch((error) => {
        if (!isActive) return;
        console.error('Geo-fence fetch error:', error);
        message.error('Failed to load geo-fence');
      })
      .finally(() => {
        if (isActive) setIsLoadingFence(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    location?.hasPerShipmentFence,
    location?.id,
    location?.stopKey,
    open,
    shipmentId,
    loadPolygonSession,
  ]);

  const recipientTeams = useMemo<GeoFenceRecipientTeamPayload[]>(
    () => buildGeoFenceRecipientTeams(selectedRecipients, recipientsValues),
    [recipientsValues, selectedRecipients]
  );
  const additionalRecipientPayload = useMemo(
    () =>
      getUniqueValues([
        ...additionalRecipients,
        ...selectedRecipients.filter((recipient) => !recipient.includes('::')),
      ]),
    [additionalRecipients, selectedRecipients]
  );

  // Polygon and notification/recipient config are one combined record
  // server-side, so undoing to empty removes the whole override.
  const handleRemoveFence = async () => {
    const token = checkTokenExpiry();
    if (!token) return;

    setIsRemovingFence(true);
    try {
      const response = await DeleteShipmentGeoFence(
        token,
        shipmentId,
        activeLocation.stopKey ?? activeLocation.id
      );
      const data = (
        response as {
          data?: { data?: { is_success?: boolean; message?: string } };
        }
      )?.data?.data;

      if (data?.is_success === false) {
        message.error(data?.message || 'Failed to remove geo-fence');
        return;
      }

      onRemoved?.(activeLocation.id);
      message.success(data?.message || 'Geo-fence removed for this shipment');
      onClose();
    } catch {
      message.error('Failed to remove geo-fence');
    } finally {
      setIsRemovingFence(false);
    }
  };

  const handleApply = async () => {
    if (polygonSession.isEmpty) {
      // Nothing to remove server-side unless this location had a saved
      // override — a fresh modal with nothing drawn just closes.
      if (!location?.hasPerShipmentFence) {
        onClose();
        return;
      }
      await handleRemoveFence();
      return;
    }

    if (!polygonSession.isValid) {
      setValidationMessage('Close the polygon before applying this geo-fence.');
      return;
    }

    if ((entersFence || exitsFence) && selectedRecipients.length === 0) {
      setValidationMessage(
        'Select at least one recipient when notifications are enabled.'
      );
      return;
    }
    const token = checkTokenExpiry();
    if (!token) return;

    const draft: GeoFenceDraft = {
      polygon: polygonSession.polygon,
      closed: polygonSession.isClosed,
      entersFence,
      exitsFence,
      selectedRecipients,
      recipientsValues,
      additionalRecipients: additionalRecipientPayload,
      isMarkAsDelivered: isMarkAsDelivered,
    };

    setIsSaving(true);
    try {
      const response = await SaveShipmentGeoFence(
        token,
        shipmentId,
        activeLocation.stopKey ?? activeLocation.id,
        {
          polygon: polygonSession.polygon,
          notify_on_enter: entersFence,
          notify_on_exit: exitsFence,
          recipients: {
            additional: additionalRecipientPayload,
            teams: recipientTeams,
          },
          mark_as_delivered: isMarkAsDelivered,
        }
      );
      const data = response?.data?.data;

      if (data?.is_success === false) {
        message.error(data?.message || 'Failed to save geo-fence');
        return;
      }

      onApply(activeLocation.id, draft);
      message.success(data?.message || 'Geo-fence saved for this shipment');
      onClose();
    } catch {
      message.error('Failed to save geo-fence');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDefaultFenceClick = () => {
    if (!activeLocation.locationId) return;
    onClose();
    navigate(
      `/settings/location?openDrawer=true&id=${activeLocation.locationId}`
    );
  };

  if (!location) return null;

  const locationName = activeLocation.name.toLowerCase();
  const locationAddress = activeLocation.address.toLowerCase();
  const notificationOptions = [
    { label: 'Enters geo-fence', value: ENTERS_FENCE },
    { label: 'Exits geo-fence', value: EXITS_FENCE },
  ];
  const selectedNotificationOptions = [
    ...(entersFence ? [ENTERS_FENCE] : []),
    ...(exitsFence ? [EXITS_FENCE] : []),
  ];
  const modalTitle = activeLocation.hasPerShipmentFence
    ? 'Edit geo-fence'
    : 'Create geo-fence';

  return (
    <>
      <CustomModalWithoutButton
        modalOpen={open}
        setModalOpen={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        modalTitle={modalTitle}
        modalSize={656}
        modalBorderRadius='16px'
        fontSize='24px'
        lineHeight='32px'
        closeIconSize='16px'
        className='geo-fence-modal'
      >
        <Flex vertical gap={20}>
          <Flex vertical gap={20}>
            <p className='geo-fence-location-copy'>
              <strong>{locationName}</strong>
              <span> &bull; {locationAddress}</span>
            </p>

            {activeLocation.hasDefaultFence && (
              <div className='geo-fence-info-banner'>
                <InfoCircleFilled className='geo-fence-info-icon' />
                <p className='geo-fence-info-copy'>
                  This location has a{' '}
                  <button
                    type='button'
                    className='geo-fence-info-link-button'
                    onClick={handleDefaultFenceClick}
                  >
                    default geo-fence
                  </button>
                  . Your fence overrides it for this shipment only.
                </p>
              </div>
            )}
          </Flex>

          <Flex vertical gap={4}>
            <div className='polygon-map-shell'>
              <PolygonFenceMap
                center={activeLocation.coordinates}
                polygon={polygonSession.polygon}
                closed={polygonSession.isClosed}
                resetTrigger={polygonSession.resetTrigger}
                onPolygonChange={(nextPolygon) => {
                  polygonSession.setPolygon(nextPolygon);
                  setValidationMessage('');
                }}
                onClosedChange={(closed) => {
                  polygonSession.setIsClosed(closed);
                  setValidationMessage('');
                }}
              />
              {polygonSession.polygon.length > 0 && (
                <button
                  type='button'
                  className='polygon-map-undo-btn'
                  onClick={() => {
                    polygonSession.undo();
                    setValidationMessage('');
                  }}
                >
                  <UndoIcon size={12} />
                  <span>Undo</span>
                </button>
              )}
              {isLoadingFence && (
                <div className='polygon-map-loading'>Loading geo-fence...</div>
              )}
            </div>
            {validationMessage && (
              <CustomShipmentText
                text={validationMessage}
                fontSize='12px'
                lineHeight='16px'
                color='#cf1322'
                marginTop='6px'
              />
            )}
          </Flex>

          <Flex vertical className='geo-fence-notification-section'>
            <CustomShipmentText
              text='Send email notification when shipment'
              fontSize='14px'
              lineHeight='16px'
              fontWeight={600}
              color='var(--text-primary)'
              marginBottom='0'
            />
            <CustomShipmentText
              text='Each notification sends once per shipment. No notifications after the shipment is marked as delivered.'
              fontSize='12px'
              lineHeight='20px'
              fontWeight={400}
              color='var(--text-secondary)'
              marginBottom='4px'
            />
            <CustomCheckboxGroup
              style={{ gap: 0 }}
              options={notificationOptions}
              selectedValues={selectedNotificationOptions}
              onChange={(values) => {
                setEntersFence(values.includes(ENTERS_FENCE));
                setExitsFence(values.includes(EXITS_FENCE));
              }}
            />
          </Flex>

          <Form form={form} initialValues={{ notification_method: 'EMAIL' }}>
            <Flex vertical gap={4}>
              <CustomShipmentText
                text='Recipients'
                fontSize='14px'
                lineHeight='16px'
                fontWeight={600}
                color='var(--text-primary)'
              />
              <div className='geo-fence-recipient-select-frame'>
                <AlertRecipientSelector
                  alertType='AT_LOCATION'
                  form={form}
                  loggedInUserId={loggedInUserId}
                  recipientData={recipientData}
                  isRecipientsDropdownOpen={isRecipientsDropdownOpen}
                  setIsRecipientsDropdownOpen={setIsRecipientsDropdownOpen}
                  sendTeamsToParent={() => {}}
                  notificationMethod='EMAIL'
                  showAdditionalTP={false}
                  selectedRecipients={selectedRecipients}
                  setSelectedRecipients={setSelectedRecipients}
                  setRecipientsValues={setRecipientsValues}
                  setAdditional={setAdditionalRecipients}
                  recipientsValues={recipientsValues}
                  suffixIcon={
                    <ChevronDownIcon size={16} color='var(--text-secondary)' />
                  }
                />
              </div>
            </Flex>
          </Form>
          {location?.stopKey?.toLowerCase() === 'destination' && (
            <>
              <Flex align='center' gap={8}>
                <Tooltip
                  title={
                    canMarkAsDelivered
                      ? ''
                      : 'Draw a geo-fence and enable "Enters geo-fence" to use this'
                  }
                >
                  <span style={{ display: 'inline-block', marginLeft: '-6px' }}>
                    <ToggleSwitch
                      checked={isMarkAsDelivered}
                      disabled={!canMarkAsDelivered}
                      onChange={(e) => {
                        setIsMarkAsDelivered(e.target.checked);
                      }}
                    />
                  </span>
                </Tooltip>
                <CustomShipmentText
                  text='Mark shipment as delivered when it enters the geo-fence'
                  fontSize='14px'
                  fontWeight={600}
                />
              </Flex>
            </>
          )}

          <Flex
            justify='space-between'
            align='center'
            gap={16}
            className='geo-fence-actions'
          >
            {/* Empty spacer to keep Cancel/Apply right-aligned via
                justify='space-between' now that there's no left-side
                "Remove fence" control — Undo, repeated until nothing is
                left, is the only way to clear a polygon now. */}
            <div />
            <Flex align='center' gap={16}>
              <CustomButton
                text='Cancel'
                onClick={onClose}
                disabled={isSaving || isLoadingFence || isRemovingFence}
                bgColor='transparent'
                color='var(--text-primary)'
                height='36px'
                minWidth='60px'
                fontSize='14px'
                fontWeight={600}
                lineHeight='16px'
                boxShadow='none'
                padding='10px 12px'
              />
              <CustomButton
                text='Apply'
                onClick={handleApply}
                loader={
                  isSaving || isRemovingFence ? <Spin size='small' /> : null
                }
                disabled={isSaving || isLoadingFence || isRemovingFence}
                bgColor='#000'
                color='#fff'
                height='36px'
                minWidth='64px'
                borderRadius='8px'
                fontSize='14px'
                fontWeight={600}
                lineHeight='16px'
                boxShadow='none'
                padding='10px 12px'
              />
            </Flex>
          </Flex>
        </Flex>
      </CustomModalWithoutButton>
    </>
  );
};

export default GeoFenceLocationsModal;
